import React, { useState, useEffect } from 'react';
import { Lock, Key, Shield, AlertCircle, Eye, EyeOff } from 'lucide-react';
import api from '../../../api';
import './AccountSections.css';

const SecuritySection = ({ userId }) => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [loginActivities, setLoginActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSecurityData();
  }, [userId]);

  const fetchSecurityData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch security settings
      const settingsResponse = await api.get(`/brands/${userId}/security/settings`);
      setTwoFactorEnabled(settingsResponse.data.twoFactorEnabled || false);

      // Fetch login activity
      const activityResponse = await api.get(`/brands/${userId}/security/login-activity?limit=10`);
      setLoginActivities(activityResponse.data || []);
    } catch (err) {
      let errorMessage = 'Failed to load security settings';
      if (err.response) {
        if (err.response.status === 403) {
          errorMessage = 'Access denied. Please log in again.';
        } else if (err.response.status === 404) {
          errorMessage = err.response.data?.message || 'User not found';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match!' });
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long!' });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const response = await api.post(`/brands/${userId}/security/change-password`, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      let errorMessage = 'Failed to change password. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTwoFactor = async () => {
    try {
      const newValue = !twoFactorEnabled;

      const response = await api.post(`/brands/${userId}/security/two-factor`, {
        enabled: newValue
      });
      setTwoFactorEnabled(newValue);
      setMessage({
        type: 'success',
        text: newValue ? 'Two-factor authentication enabled' : 'Two-factor authentication disabled'
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to update two-factor authentication'
      });
    }
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h2 className="section-title">Security Settings</h2>
        <p className="section-description">Manage your account security</p>
      </div>

      {loading && (
        <div className="loading-state">
          <p>Loading security settings...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>{error}</p>
        </div>
      )}

      {message.text && (
        <div className={`message-state ${message.type}`}>
          <AlertCircle size={18} />
          {message.text}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Change Password */}
          <div className="security-section">
        <h3 className="section-subtitle">
          <Lock size={20} className="inline mr-2" />
          Change Password
        </h3>

        <form onSubmit={handlePasswordSubmit} className="security-form">
          <div className="form-group">
            <label htmlFor="currentPassword" className="form-label">
              Current Password
            </label>
            <div className="password-input-wrapper">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                className="form-input"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">
              New Password
            </label>
            <div className="password-input-wrapper">
              <input
                type={showNewPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                className="form-input"
                required
                minLength={8}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <p className="form-hint">Must be at least 8 characters long</p>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm New Password
            </label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                className="form-input"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>

      {/* Two-Factor Authentication */}
      <div className="security-section">
        <h3 className="section-subtitle">
          <Shield size={20} className="inline mr-2" />
          Two-Factor Authentication
        </h3>

        <div className="setting-item">
          <div className="setting-info">
            <Key size={20} className="text-gray-500" />
            <div>
              <p className="setting-label">Two-Factor Authentication</p>
              <p className="setting-description">
                Add an extra layer of security to your account
              </p>
            </div>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={twoFactorEnabled}
              onChange={handleToggleTwoFactor}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Login Activity */}
      <div className="security-section">
        <h3 className="section-subtitle">Recent Login Activity</h3>

        {loginActivities.length > 0 ? (
          <div className="activity-list">
            {loginActivities.map((activity) => (
              <div key={activity.id} className="activity-log-item">
                <div className="activity-log-info">
                  <p className="activity-log-device">{activity.device}</p>
                  <p className="activity-log-location">{activity.location}</p>
                </div>
                <p className="activity-log-time">{activity.time}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 p-4">No recent login activity</p>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default SecuritySection;

